/**
 * SplineNode - Create 2D shapes from control points
 *
 * Outputs a Three.js Shape that can be used with ExtrudeNode or LatheNode.
 * Points are defined as a flat array: [x1,y1, x2,y2, ...]
 */

import { BaseNode } from '@nodes/BaseNode';
import { internals, params } from '@/nodes/TypedEndpoints';
import type { NodeDef } from '@runtime/IRuntime';
import type { ThreeDependencies } from '../threejsPlugin';
import type { Three } from '../types';

const splineEndpoints = {
  ...internals({
    shapeOut: { dataType: 'three:shape', isOutput: true }
  }),
  ...params({
    // Default: simple rectangle - array of [x, y] pairs (also accepts polylines)
    points: {
      dataType: 'array|polylines',
      value: [[-1, -1], [1, -1], [1, 1], [-1, 1]],
      label: 'Points'
    },
    closed: { dataType: 'boolean', value: true },
    smooth: { dataType: 'boolean', value: false, label: 'Smooth Curve' },
    tension: { value: 0.5, min: 0, max: 1, step: 0.01, label: 'Curve Tension' },
    plane: { dataType: 'string', value: 'XY' as const, options: ['XY', 'XZ'] as const, label: 'Plane' }
  })
};

class SplineNode extends BaseNode<typeof splineEndpoints> {
  private three: Three;

  constructor(id: string, deps: ThreeDependencies) {
    super(id, deps);
    this.three = deps.three;
  }

  initialize(): void {
    super.initialize();
    this.generateShape();
  }

  private parsePoints(): { x: number; y: number }[] {
    const arr = this.params.points.value;
    if (!Array.isArray(arr)) {
      throw new Error('SplineNode: points must be an array of [x, y] pairs or polylines');
    }

    // Polylines format: [{x,y,z}, ...][]
    if (arr.length > 0 && Array.isArray(arr[0]) && arr[0].length > 0
        && typeof arr[0][0] === 'object' && 'x' in arr[0][0]) {
      return arr[0].map((p: any) => ({ x: Number(p.x), y: Number(p.y) }));
    }

    // Standard format: [[x,y], ...]
    return arr
      .filter((p: any) => Array.isArray(p) && p.length >= 2)
      .map((p: any) => ({ x: Number(p[0]), y: Number(p[1]) }));
  }

  private generateShape(): void {
    if (!this.three || !this.three.Shape) {
      throw new Error('SplineNode: Three.js not available');
    }

    const points = this.parsePoints();
    if (points.length < 2) {
      this.internals.shapeOut.set(null);
      return;
    }

    const shape = new this.three.Shape();

    if (this.params.smooth.value && points.length >= 3) {
      // Use spline curve
      const vec2Points = points.map(p => new this.three.Vector2(p.x, p.y));
      const curve = new this.three.SplineCurve(vec2Points);
      const smoothPoints = curve.getPoints(points.length * 10);

      shape.moveTo(smoothPoints[0].x, smoothPoints[0].y);
      for (let i = 1; i < smoothPoints.length; i++) {
        shape.lineTo(smoothPoints[i].x, smoothPoints[i].y);
      }
    } else {
      // Straight lines between points
      shape.moveTo(points[0].x, points[0].y);
      for (let i = 1; i < points.length; i++) {
        shape.lineTo(points[i].x, points[i].y);
      }
    }

    if (this.params.closed.value) {
      shape.closePath();
    }

    this.internals.shapeOut.set({ value: shape, plane: this.params.plane.value as 'XY' | 'XZ' });
  }

  onParamChanged(): void {
    this.generateShape();
  }
}

export const splineNodeDef: NodeDef<SplineNode> = {
  type: 'Spline2D',
  shortName: 'spline',
  category: 'shape',
  info: 'Click to add points\nDrag to move points\nRight-click to delete\nScroll to zoom\n\u2318+drag to pan',
  endpoints: splineEndpoints,
  ctor: SplineNode
};
